import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { Network, Radio, Search, Sparkles, UsersRound } from "lucide-react";
import { useMemo, useState } from "react";

import GraphCanvas from "#/components/GraphCanvas";
import { getGraphData } from "#/lib/bookling";
import { GRAPH_ENABLED } from "#/lib/feature-flags";
import { includesQuery, normalizedQuery } from "#/lib/search";
import { SITE_NAME, absoluteUrl, canonicalLink, jsonLdScript, seoMeta } from "#/lib/seo";

import type { GraphSelection } from "#/components/GraphCanvas";
import type { GraphData, GuestGraphEdge } from "#/lib/bookling-data";

type GraphMode = "combined" | "books" | "references";

const MODE_OPTIONS: ReadonlyArray<{ value: GraphMode; label: string }> = [
  { value: "combined", label: "Combined" },
  { value: "books", label: "Shared books" },
  { value: "references", label: "Episode mentions" },
];

export const Route = createFileRoute("/graph")({
  beforeLoad: () => {
    if (!GRAPH_ENABLED) throw notFound();
  },
  loader: () => getGraphData(),
  head: ({ loaderData }) => {
    const description =
      "Explore the GDIY guest graph through shared books and cross-episode references.";

    return {
      meta: seoMeta({
        title: `Graph | ${SITE_NAME}`,
        description,
        path: "/graph",
      }),
      links: canonicalLink("/graph"),
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "GDIY interaction graph",
          description,
          url: absoluteUrl("/graph"),
          about: {
            "@type": "Dataset",
            name: "Bookling GDIY guest graph",
            description:
              "Guests connected by shared book mentions and explicit references to other GDIY episodes.",
            variableMeasured: ["guest", "shared book", "episode reference", "connection count"],
            measurementTechnique: "Podcast episode book mentions",
            size: loaderData
              ? `${loaderData.stats.guestCount} guests and ${loaderData.stats.sharedBookEdgeCount} shared-book links`
              : undefined,
          },
        }),
      ],
    };
  },
  component: GraphPage,
});

function GraphPage() {
  const graph = Route.useLoaderData();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<GraphMode>("combined");
  const [minSharedBooks, setMinSharedBooks] = useState(2);
  const [selected, setSelected] = useState<GraphSelection>(null);

  const filteredGraph = useMemo(
    () => filterGraph(graph, { query, mode, minSharedBooks }),
    [graph, query, mode, minSharedBooks],
  );

  const highlightedNodeId = useMemo(() => {
    const q = normalizedQuery(query);
    if (!q) return null;
    return filteredGraph.nodes.find((node) => includesQuery(node.guest, q))?.id ?? null;
  }, [filteredGraph.nodes, query]);

  return (
    <main>
      <section className="hero-band">
        <div className="page-wrap hero-layout">
          <div className="hero-copy">
            <p className="hero-kicker">
              <span className="hero-kicker-mark" aria-hidden="true" />
              GDIY interaction graph
            </p>
            <h1>
              The archive,
              <br />
              <em>as a living network.</em>
            </h1>
            <p className="hero-lede">
              Guests connect when they cite the same books or explicitly point listeners to another
              episode. Follow the references that make the GDIY archive compound.
            </p>
          </div>

          <aside className="hero-flag" aria-label="Graph status">
            <p className="hero-flag-label">Graph volume</p>
            <strong className="hero-flag-figure">{graph.stats.guestCount}</strong>
            <p className="hero-flag-caption">
              guests <span aria-hidden="true">·</span> {graph.stats.sharedBookEdgeCount} links
            </p>
          </aside>
        </div>
      </section>

      <section className="page-wrap graph-page" aria-label="GDIY graph explorer">
        <GraphInsights graph={graph} />

        <div className="graph-toolbar">
          <label className="library-search graph-search">
            <Search className="h-4 w-4" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search guest or book"
              aria-label="Search graph"
            />
          </label>

          <div
            className="sort-control graph-mode-control"
            role="radiogroup"
            aria-label="Graph mode"
          >
            {MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={mode === option.value}
                className={mode === option.value ? "sort-pill is-active" : "sort-pill"}
                onClick={() => setMode(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="graph-range">
            <span>Min shared books</span>
            <select
              value={minSharedBooks}
              onChange={(event) => setMinSharedBooks(Number(event.target.value))}
              disabled={mode === "references"}
            >
              {[1, 2, 3, 5].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>

        {graph.nodes.length === 0 ? (
          <EmptyGraphNotice />
        ) : (
          <div className="graph-shell">
            <GraphCanvas
              nodes={filteredGraph.nodes}
              edges={filteredGraph.edges}
              selected={selected}
              highlightedNodeId={highlightedNodeId}
              onSelect={setSelected}
            />
            <GraphInspector selection={selected} graph={filteredGraph} onSelectNode={setSelected} />
          </div>
        )}
      </section>
    </main>
  );
}

function filterGraph(
  graph: GraphData,
  options: { query: string; mode: GraphMode; minSharedBooks: number },
): Pick<GraphData, "nodes" | "edges"> {
  const q = normalizedQuery(options.query);
  const edges = graph.edges.filter((edge) => {
    if (options.mode === "books" && edge.sharedBookCount === 0) return false;
    if (options.mode === "references" && edge.referenceCount === 0) return false;
    if (
      options.mode !== "references" &&
      edge.sharedBookCount > 0 &&
      edge.sharedBookCount < options.minSharedBooks
    ) {
      return false;
    }
    if (!q) return true;
    return edgeMatchesQuery(edge, q);
  });

  const activeNodeIds = new Set<string>();
  for (const edge of edges) {
    activeNodeIds.add(edge.source);
    activeNodeIds.add(edge.target);
  }

  let nodes = graph.nodes.filter((node) => activeNodeIds.has(node.id));
  if (!q && nodes.length > 150) {
    const keep = new Set(nodes.slice(0, 150).map((node) => node.id));
    nodes = nodes.filter((node) => keep.has(node.id));
    return {
      nodes,
      edges: edges.filter((edge) => keep.has(edge.source) && keep.has(edge.target)).slice(0, 420),
    };
  }

  return { nodes, edges: edges.slice(0, q ? 260 : 520) };
}

function edgeMatchesQuery(edge: GuestGraphEdge, query: string) {
  return (
    includesQuery(edge.sourceGuest, query) ||
    includesQuery(edge.targetGuest, query) ||
    edge.sharedBooks.some(
      (book) => includesQuery(book.title, query) || includesQuery(book.author, query),
    ) ||
    edge.references.some(
      (reference) =>
        includesQuery(reference.sourceEpisodeTitle, query) ||
        includesQuery(reference.targetEpisodeTitle, query) ||
        includesQuery(reference.context, query),
    )
  );
}

function EmptyGraphNotice() {
  return (
    <div className="graph-empty-state" role="status">
      <p className="eyebrow">No graph data</p>
      <h2>Import the scraper data to render the GDIY network.</h2>
      <p>
        The graph no longer uses sample data. Run the D1 migration and import pipeline so episodes,
        books, and episode links can be visualized.
      </p>
    </div>
  );
}

function GraphInsights({ graph }: { graph: GraphData }) {
  const strongest = graph.stats.strongestBookPairs[0];
  const referenced = graph.stats.mostReferencedGuests[0];
  const connectorBook = graph.stats.mostSharedBooks[0];
  const bridge = graph.stats.bridgeGuests[0];

  return (
    <div className="graph-insights" aria-label="Graph insights">
      <InsightCard
        icon={<Network className="h-5 w-5" aria-hidden="true" />}
        label="Strongest overlap"
        value={strongest ? `${strongest.sourceGuest} ↔ ${strongest.targetGuest}` : "No pair yet"}
        detail={
          strongest
            ? `${strongest.sharedBookCount} shared books`
            : "Import more mentions to build links."
        }
      />
      <InsightCard
        icon={<Radio className="h-5 w-5" aria-hidden="true" />}
        label="Most referenced"
        value={referenced?.guest ?? "No references yet"}
        detail={
          referenced
            ? `${referenced.referenceInCount} incoming episode mentions`
            : "Episode links are optional."
        }
      />
      <InsightCard
        icon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
        label="Connector book"
        value={connectorBook?.title ?? "No connector yet"}
        detail={
          connectorBook
            ? `${connectorBook.guestCount ?? 0} guests · ${connectorBook.connectorScore} guest pairs`
            : "Shared books create guest edges."
        }
      />
      <InsightCard
        icon={<UsersRound className="h-5 w-5" aria-hidden="true" />}
        label="Bridge guest"
        value={bridge?.guest ?? "No bridge yet"}
        detail={
          bridge
            ? `${bridge.connectionCount} connections · ${bridge.bookCount} books`
            : "Connections appear after filtering."
        }
      />
    </div>
  );
}

function InsightCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <article className="graph-insight-card">
      <div className="graph-insight-icon">{icon}</div>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </article>
  );
}

function GraphInspector({
  selection,
  graph,
  onSelectNode,
}: {
  selection: GraphSelection;
  graph: Pick<GraphData, "nodes" | "edges">;
  onSelectNode: (selection: GraphSelection) => void;
}) {
  if (!selection) {
    return (
      <aside className="graph-inspector">
        <p className="eyebrow">Inspector</p>
        <h2>Click a guest or relationship.</h2>
        <p>
          The panel will explain why two guests are connected: shared books, episode references, or
          both.
        </p>
        <div className="graph-mini-stats">
          <span>{graph.nodes.length} visible guests</span>
          <span>{graph.edges.length} visible relationships</span>
        </div>
      </aside>
    );
  }

  if (selection.type === "edge") {
    return <EdgeInspector edge={selection.edge} />;
  }

  const node = selection.node;
  const relatedEdges = graph.edges
    .filter((edge) => edge.source === node.id || edge.target === node.id)
    .slice(0, 8);

  return (
    <aside className="graph-inspector">
      <div className="graph-inspector-guest-head">
        <div className="graph-inspector-avatar" aria-hidden="true">
          {node.guestImage ? (
            <img src={node.guestImage} alt="" loading="lazy" decoding="async" />
          ) : (
            <span>{node.guest.slice(0, 1).toUpperCase()}</span>
          )}
        </div>
        <div>
          <p className="eyebrow">Guest node</p>
          <h2>{node.guest}</h2>
          {node.company ? <p className="graph-inspector-company">{node.company}</p> : null}
        </div>
      </div>
      <div className="graph-mini-stats graph-mini-stats--grid">
        <span>{node.connectionCount} connections</span>
        <span>{node.bookCount} books</span>
        <span>{node.referenceInCount} referenced by</span>
        <span>{node.referenceOutCount} mentions made</span>
      </div>

      <section className="graph-inspector-section">
        <h3>Episodes</h3>
        <ul>
          {node.episodeNumbers.slice(0, 5).map((number, index) => (
            <li key={number}>
              #{number} · {node.episodeTitles[index] ?? "GDIY episode"}
            </li>
          ))}
        </ul>
      </section>

      <section className="graph-inspector-section">
        <h3>Strongest visible neighbors</h3>
        <ul>
          {relatedEdges.map((edge) => {
            const neighborId = edge.source === node.id ? edge.target : edge.source;
            const neighbor = graph.nodes.find((candidate) => candidate.id === neighborId);
            return (
              <li key={edge.id}>
                <button
                  type="button"
                  className="graph-neighbor-button"
                  onClick={() => neighbor && onSelectNode({ type: "node", node: neighbor })}
                >
                  {neighbor?.guest ?? edge.targetGuest}
                  <span>
                    {edge.sharedBookCount} books · {edge.referenceCount} refs
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </aside>
  );
}

function EdgeInspector({ edge }: { edge: GuestGraphEdge }) {
  return (
    <aside className="graph-inspector">
      <p className="eyebrow">Relationship</p>
      <h2>
        {edge.sourceGuest} ↔ {edge.targetGuest}
      </h2>
      <div className="graph-mini-stats graph-mini-stats--grid">
        <span>{edge.sharedBookCount} shared books</span>
        <span>{edge.referenceCount} episode mentions</span>
      </div>

      {edge.sharedBooks.length > 0 ? (
        <section className="graph-inspector-section">
          <h3>Shared books</h3>
          <ul>
            {edge.sharedBooks.slice(0, 8).map((book) => (
              <li key={book.id}>
                <Link to="/books/$slug" params={{ slug: book.slug }}>
                  {book.title}
                </Link>
                <span> by {book.author}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {edge.references.length > 0 ? (
        <section className="graph-inspector-section">
          <h3>Episode references</h3>
          <ul>
            {edge.references.slice(0, 8).map((reference) => (
              <li
                key={`${reference.sourceEpisodeNumber}-${reference.targetEpisodeNumber}-${reference.context ?? ""}`}
              >
                <strong>
                  #{reference.sourceEpisodeNumber} → #{reference.targetEpisodeNumber}
                </strong>
                <span>
                  {" "}
                  {reference.sourceGuest} mentions {reference.targetGuest}
                </span>
                {reference.context ? <em>{reference.context}</em> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
}
